import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useScanner from '../hooks/useScanner';
import { cardPhotoReady } from '../utils/cardPhotoBridge';
import '../styles/scanButton.css';

/** How many times a tap on the button tries to read the card from a photo before giving up (C20 Pro). */
export const PHOTO_ATTEMPTS = 2;

/**
 * A 44px scan button: starts the native scanner (the side key does the same). Renders nothing in a
 * browser or an app without a scanner. While scanning it reads "Scanning..." and a second tap stops
 * the scan. The scanned code is handled by the ScanListener (window.onBarcodeScanned), not here.
 */
const ScanButton = ({ className = '' }) => {
  const { available, scanning, start, stop, lastState } = useScanner();
  const startedHere = useRef(false);
  // On the Android till with a camera (the C20 Pro) a tap photographs the rewards card and the server reads the number off the photo: up to
  // PHOTO_ATTEMPTS tries per tap. If every try fails the NEXT tap uses the barcode scanner instead (scanFallback), then it is photo-first again.
  // Everywhere else (browser, handheld, iOS, an app without capturePhoto) this is all inert and the button is the plain scan button it always was.
  const [photoBusy, setPhotoBusy] = useState(false);
  const [scanFallback, setScanFallback] = useState(false);
  const photoFirst = cardPhotoReady() && !scanFallback;

  // Messages only for a scan THIS button started (several buttons can be on screen).
  useEffect(() => {
    if (!lastState || !startedHere.current) return;
    if (lastState.state === 'timeout') {
      toast.info('Nothing scanned');
      startedHere.current = false;
    } else if (lastState.state === 'error') {
      toast.error(`Scanner not available: ${lastState.message || (lastState.error !== undefined ? `error ${lastState.error}` : 'unknown error')}`);
      startedHere.current = false;
    } else if (lastState.state === 'idle' || lastState.state === 'cancelled') {
      startedHere.current = false;
    }
  }, [lastState]);

  if (!available && !cardPhotoReady()) return null;

  const readFromPhotos = async () => {
    if (photoBusy) return;
    setPhotoBusy(true);
    let done = false;
    try {
      // loaded only now: the server half pulls in the API layer, which the scan button does not otherwise need
      const { photographAndLookUpCard } = await import('../utils/cardPhoto');
      for (let attempt = 1; attempt <= PHOTO_ATTEMPTS && !done; attempt += 1) {
        try {
          // true: a number was read and handed to the normal lookup; false: the cashier backed out of the camera, so no second try
          const ok = await photographAndLookUpCard();
          done = true;
          if (!ok) return;
        } catch (err) {
          const message = err?.message || 'Could not read the card from the photo';
          if (attempt < PHOTO_ATTEMPTS) toast.error(`${message} Trying again (${attempt + 1} of ${PHOTO_ATTEMPTS}).`);
          else {
            toast.error(message);
            if (available) {
              toast.info('Tap again to use the barcode scanner instead.');
              setScanFallback(true);
            }
          }
        }
      }
    } finally {
      setPhotoBusy(false);
    }
  };

  const onClick = () => {
    if (photoFirst) {
      readFromPhotos();
      return;
    }
    if (scanFallback) setScanFallback(false); // one scanner try, then back to the photo
    if (scanning) {
      stop();
      return;
    }
    startedHere.current = true; // a failure to start arrives through lastState as an error
    start();
  };

  return (
    <button
      type="button"
      className={`scanBtn${scanning ? ' scanning' : ''}${photoFirst || photoBusy ? ' photo' : ''} ${className}`.trim()}
      aria-label={photoFirst || photoBusy ? 'Take a photo of the card' : 'Scan barcode'}
      aria-pressed={scanning}
      title={photoBusy ? 'Reading the card...' : photoFirst ? 'Take a photo of the rewards card' : scanning ? 'Scanning... tap to stop' : 'Scan barcode'}
      disabled={photoBusy}
      onClick={onClick}
    >
      {photoFirst || photoBusy ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 8a2 2 0 0 1 2-2h2l1.5-2h5L16 6h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <circle cx="12" cy="12.5" r="3.2" stroke="currentColor" strokeWidth="2" />
        </svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M7 8v8M10 8v8M13 8v8M16 8v8M18.5 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      )}
      {scanning && <span className="scanBtnLabel">Scanning...</span>}
      {photoBusy && <span className="scanBtnLabel">Reading...</span>}
    </button>
  );
};

export default ScanButton;
